import AdminHeader from "components/Headers/AdminHeader";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Input,
} from "reactstrap";
import { getCourseStatusColor } from "constants/CommonConstants";
import { renderStars } from "components/CommonComponents/CommonFunctions";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { COURSE_CONFIG } from "components/constant";
import { ISOToHumanReadableDate } from "components/CommonComponents/CommonFunctions";
import { canShowPurchaseButton } from "components/constant";
import { coursesList } from "components/constant";

const PRICE_FILTERS = [
  { label: "All", count: 94 },
  { label: "Paid", count: 65 },
  { label: "Free", count: 32 },
];

const LEVEL_FILTERS = [
  { label: "All", count: 40 },
  { label: "Beginner", count: 4 },
  { label: "Intermediate", count: 3 },
  { label: "Advanced", count: 2 },
];

const REVIEW_FILTERS = [
  { stars: 5, count: 300 },
  { stars: 4, count: 454 },
  { stars: 3, count: 202 },
  { stars: 2, count: 200 },
  { stars: 1, count: 122 },
];

const COURSE_TYPE_FILTERS = [
  {
    label: "All",
    count: 1100,
  },
  {
    label: "Recorded",
    count: 500,
  },
  {
    label: "Live",
    count: 650,
    subTypes: [
      {
        label: "Offline",
        count: 250,
      },
      {
        label: "Online",
        count: 400,
      },
      {
        label: "Hybrid",
        count: 360,
      },
    ],
  },
];

const Courses = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const rolePath =
    role === "SUPER_ADMIN"
      ? "super-admin"
      : role === "ADMIN"
        ? "admin"
        : role === "CHANNEL_PARTNER"
          ? "channel-partner"
          : "instructor";
  const config = COURSE_CONFIG[role] || {};
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isMyCourse = pathname.includes("/my-courses");
  const mode = isMyCourse ? "MY_COURSE" : "COURSE";

  const [courseListState, setCourseListState] = useState({
    isLoading: true,
    isSearched: false,
    error: "",
    noRecords: false,
  });
  const [showFilters, setShowFilters] = useState(false); // to show or hide the filters UI in mobile screen
  const [filterState, setFilterState] = useState({
    searchName: "",
    price: "All", // All | Free | Paid
    level: [], // All | Beginner | Intermediate | Expert
    courseType: [], // Recorded | Live
    rating: [], //5/4/3/2/1
  });

  const [isLiveChecked, setIsLiveChecked] = useState();

  const buildFilterParams = () => {
    const filters = [];

    if (filterState.price !== "All") {
      filters.push({
        field: "AccessType",
        condition: "eq",
        filterValues: [filterState.price],
      });
    }

    if (filterState.level !== "All") {
      filters.push({
        field: "Level",
        condition: "eq",
        filterValues: [filterState.level],
      });
    }
    if (filterState.courseType !== "All") {
      filters.push({
        field: "CourseType",
        condition: "eq",
        filterValues: [filterState.courseType],
      });
    }
    if (filterState.rating !== "All") {
      filters.push({
        field: "Rating",
        condition: "gte",
        filterValues: [Number(filterState.rating)],
      });
    }

    return {
      filters: filters.length ? JSON.stringify(filters) : [],
      searchString: filterState.searchName || "",
    };
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };
  const handleSearch = () => {};

  // Single select (radio) for price
  const handleSingleSelectFilter = (key, value) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
  };

  // Multi select (checkbox)
  const handleMultiSelectFilter = (key, value) => {
    setFilterState((prev) => {
      const list = prev[key];
      if (list.includes(value)) {
        // remove
        return { ...prev, [key]: list.filter((v) => v !== value) };
      } else {
        // add
        return { ...prev, [key]: [...list, value] };
      }
    });
  };
  const handleSearchChange = (e) => {
    const { value } = e.target;

    setFilterState((prev) => ({
      ...prev,
      searchName: value,
    }));
  };

  const handleNavigation = (courseId) => {
    const targetPath = isMyCourse
      ? `/${rolePath}/my-courses/${urlEncodedId(courseId)}/details`
      : `/${rolePath}/course/${urlEncodedId(courseId)}/details`;

    navigate(targetPath, { state: { mode: mode } });
  };

  const handlePurchase = (e, course) => {
    e.stopPropagation();
    navigate(`/channel-partner/course/${urlEncodedId(course.id)}/purchase`);
  };

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6 mb-4">
        <div className="d-flex align-items-center mb-4">
          <h3 className="mb-0 mt-4 text-white-input flex-shrink-0">Courses</h3>

          <div className="d-flex align-items-center ml-auto">
            <InputGroup className="mr-4 flex-grow-1 search-bottom-border">
              <Input
                type="text"
                placeholder="Search title, category"
                className="text-white-input"
                value={filterState.searchName}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
              />
              <InputGroupAddon addonType="append">
                <InputGroupText>
                  <i className="fa fa-search" />
                </InputGroupText>
              </InputGroupAddon>
            </InputGroup>

            {COURSE_CONFIG[role]?.showAddCourse && (
              <Button
                className="text-nowrap ml-2"
                onClick={() =>
                  navigate(
                    role === "SUPER_ADMIN"
                      ? "/super-admin/course/add-course"
                      : "/admin/course/add-course",
                  )
                }
              >
                <i className="fa fa-plus mr-1" />
                Add Course
              </Button>
            )}
          </div>
        </div>

        <div className="d-lg-none d-flex justify-content-end mt-5 mb-1 w-100">
          <Button
            color="primary"
            className="btn-deep-blue"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
        </div>

        <Row className="mt-5">
          {/* left card gird for courses */}
          <Col xl="9" lg="8" md="12">
            {!courseListState.isLoading ? (
              <div className="loaderContainer">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : courseListState.error ? (
              <div className="loaderContainer">{courseListState.error}</div>
            ) : courseListState.noRecords ? (
              <div className="loaderContainer text-muted">
                No courses added yet.
              </div>
            ) : (
              <div className="courses-grid">
                {coursesList.map((course) => {
                  return (
                    <div key={course.id} className="course-card-wrapper">
                      <Card
                        className="course-card mb-1 cursor"
                        onClick={() => handleNavigation(course.id)}
                      >
                        {/* Course Image */}
                        <div className="course-image-wrapper">
                          <img
                            src={course.image}
                            alt={course.title}
                            className="course-image"
                          />

                          {/* Course Type Badge */}
                          <span
                            className={`radio-badge image-radio-badge ${
                              course.courseType === "Live" ? "live" : "recorded"
                            }`}
                          >
                            <span className="radio-circle">
                              <span className="radio-dot" />
                            </span>
                            {course.courseType}
                          </span>

                          {/* Status Badge */}
                          <Badge
                            color={getCourseStatusColor(course.status)}
                            className="course-status-badge"
                          >
                            {course.status}
                          </Badge>
                        </div>

                        {/* Course Details */}
                        <CardBody className="pb-2">
                          <div className="d-flex justify-content-between small text-muted">
                            {COURSE_CONFIG[role]?.showCreatedBy ? (
                              <div>
                                Created by <strong>{course.createdBy}</strong>
                              </div>
                            ) : (
                              <div>
                                <div className="text-muted small">
                                  Course Title
                                </div>

                                <h4 className="course-title mb-3">
                                  {course.title}
                                </h4>
                              </div>
                            )}

                            <div>
                              {ISOToHumanReadableDate(course.createdOn)}
                            </div>
                          </div>

                          {/* Course Title */}
                          {COURSE_CONFIG[role]?.showCreatedBy ? (
                            <div>
                              <div className="text-muted small mt-3">
                                Course Title
                              </div>

                              <h4 className="course-title mb-3">
                                {course.title}
                              </h4>
                            </div>
                          ) : null}

                          {/* Code and Category */}
                          <div className="d-flex justify-content-between mb-3">
                            <div>
                              <div className="text-muted small">
                                {COURSE_CONFIG[role]?.showCode
                                  ? "Code"
                                  : "Sessions"}
                              </div>

                              <strong>
                                {COURSE_CONFIG[role]?.showCode
                                  ? course.code
                                  : course.sessions}
                              </strong>
                            </div>

                            <div className="text-right">
                              <div className="text-muted small">Category</div>
                              <strong>{course.category}</strong>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between">
                            <div>
                              <div className="text-muted small">
                                {COURSE_CONFIG[role]?.showRating
                                  ? "Rating"
                                  : "Enrolled Students"}
                              </div>

                              {COURSE_CONFIG[role]?.showRating ? (
                                renderStars(course.rating)
                              ) : (
                                <strong>
                                  <Link
                                    to={`/${rolePath}/course/${urlEncodedId(course.id)}/details?tab=enrollment`}
                                    className="link-reset"
                                  >
                                    {course.enrolledStudents}
                                  </Link>
                                </strong>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-muted small">Level</div>
                              <strong>{course.level}</strong>
                            </div>
                          </div>

                          {COURSE_CONFIG[role]?.showRating ? (
                            <div className="d-flex mt-3 justify-content-between">
                              <div>
                                <div className="text-muted small">
                                  Enrolled Students
                                </div>
                                <strong>
                                  <Link
                                    to={`/${rolePath}/course/${urlEncodedId(course.id)}/details?tab=enrollment`}
                                    className="link-reset"
                                  >
                                    {259 * course.id}
                                  </Link>
                                </strong>
                              </div>
                            </div>
                          ) : null}
                          <hr className="my-2" />

                          <div className="d-flex justify-content-between align-items-center mt-0 mb-0">
                            <div className="course-price">
                              {course.accessType === "Paid" ? (
                                `₹${course.price.toFixed(2)}`
                              ) : (
                                <strong>Free</strong>
                              )}
                            </div>

                            <div className="d-flex align-items-center ">
                              {/* View More */}
                              <Button
                                color="link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNavigation(course.id);
                                }}
                              >
                                View More
                              </Button>
                              {/* Purchase button – Channel Partner only */}
                              {canShowPurchaseButton(role, mode) && (
                                <Button
                                  color="default"
                                  className="btn-vivid-red p-2"
                                  onClick={(e) => handlePurchase(e, course)}
                                >
                                  Purchase
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}
          </Col>

          {/* Right: Filters */}
          <Col
            xl="3"
            lg="4"
            md="12"
            className={`filters-wrapper ${showFilters ? "show-filters" : ""}`}
          >
            <Card className="filters-card mb-4">
              <CardBody>
                <h5 className="mb-4">Filters</h5>

                {/* Price */}
                <div className="mb-4">
                  <h5 className="text-muted mb-3">Price</h5>
                  {PRICE_FILTERS.map((item) => (
                    <div
                      key={item.label}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0">
                        <input
                          type="radio"
                          name="price"
                          className="mr-2"
                          checked={filterState.price === item.label}
                          onChange={() =>
                            handleSingleSelectFilter("price", item.label)
                          }
                        />
                        {item.label}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Levels */}
                <div className="mb-4">
                  <h5 className="text-muted mb-3">Levels</h5>
                  {LEVEL_FILTERS.map((item) => (
                    <div
                      key={item.label}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={filterState.level.includes(item.label)}
                          onChange={() =>
                            handleMultiSelectFilter("level", item.label)
                          }
                        />
                        {item.label}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Reviews */}
                <div className="mb-4">
                  <h5 className="text-muted mb-3">Reviews</h5>
                  {REVIEW_FILTERS.map((item) => (
                    <div
                      key={item.stars}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="mb-0 d-flex align-items-center">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={filterState.rating.includes(
                            String(item.stars),
                          )}
                          onChange={() =>
                            handleMultiSelectFilter(
                              "rating",
                              String(item.stars),
                            )
                          }
                        />
                        {renderStars(item.stars, false)}
                      </label>
                      <span className="text-muted small">{item.count}</span>
                    </div>
                  ))}
                </div>

                {/* Course Type */}
                <div>
                  <h5 className="text-muted mb-3">Course Type</h5>

                  {COURSE_TYPE_FILTERS.map((item) => (
                    <div key={item.label} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <label className="mb-0 cursor">
                          <input
                            type="checkbox"
                            className="mr-2"
                            onChange={(e) => {
                              if (item.label === "Live") {
                                setIsLiveChecked(e.target.checked);
                              }
                            }}
                          />
                          {item.label}
                        </label>
                        <span className="text-muted small">{item.count}</span>
                      </div>

                      {/* Show sub-types only for Live */}
                      {item.label === "Live" &&
                        isLiveChecked &&
                        item.subTypes && (
                          <div className="mt-3">
                            <h5 className="text-muted mb-3">
                              Content Delivery Mode
                            </h5>
                            {item.subTypes.map((sub) => (
                              <div
                                key={sub.label}
                                className="d-flex justify-content-between align-items-center mb-1"
                              >
                                <label className="mb-0 cursor">
                                  <input type="checkbox" className="mr-2" />
                                  {sub.label}
                                </label>
                                <span className="text-muted small">
                                  {sub.count}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Courses;
